import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const onClickItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    setOpen(false);
    if (n.request_id) {
      navigate(user?.role === "professional" ? `/pro/solicitud/${n.request_id}` : `/solicitud/${n.request_id}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-bell">
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {data.unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary text-[10px] font-bold text-white flex items-center justify-center"
              data-testid="notifications-unread-count"
            >
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-heading font-semibold text-sm">Notificaciones</p>
          {data.unread > 0 && (
            <button
              className="text-xs text-primary hover:underline"
              data-testid="notifications-mark-all-read"
              onClick={async () => {
                await api.post("/notifications/read-all").catch(() => {});
                load();
              }}
            >
              Marcar todas leídas
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && (
            <p className="text-sm text-muted-foreground px-4 py-8 text-center">Sin notificaciones</p>
          )}
          {data.items.map((n) => (
            <button
              key={n.id}
              onClick={() => onClickItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-secondary transition-colors duration-200 ${
                n.read ? "opacity-60" : "bg-accent/40"
              }`}
            >
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
